import DataDatabase from '../classes/data-database';
import DataLayerTracker from '../classes/data-layer-tracker';
import DataPages from '../classes/data-pages';
import HousingReportGeoSelect from './housing-report-geo-select';
import HousingReportGrid from './housing-report-grid';
import HousingReportMap from './housing-report-map';
import HousingReportPeriodSelects from './housing-report-period-selects';

class HousingReport extends DataPages {
    CURRENT_DATABASE_SLUG = 'housing-report';

    // Define instances for various components and classes
    DatabaseDataInstance; // For working with data from a database
    PeriodSelectsInstance; // For working with period selection [ annual , monthly ]
    GridInstance; // For working with the data grid : AG Grid
    GeoSelectsInstance; // For working with geography selection
    MapInstance; // For Map
    DataLayerTrackerInstance; // For GTM tracker

    constructor() {
        super();
        //  store a reference to the same function that was added as the listener in order to remove it.
        this.boundPageInitialized = this.pageInitialized.bind(this);

        // Initialize and validate HTML variables
        let validationVariables = this._initAndValidateHTMLVariables();

        if (!validationVariables) {
            return;
        }

        // Register custom events
        this.registerCustomEvents();

        // Initialize instances of various components and classes
        this.initializeClassesInstances();

        // Initialize period selection
        this.initializePeriodSelectsInstance();

        // Initialize geography selection and other components when it's done
        this.initializeGeoSelectsInstance().then(() => {
            this.initializeGrid();
            this.initializeMap();
        });
    }

    /**
     * Register custom events for the HousingReport module.
     */
    registerCustomEvents() {
        document.addEventListener(
            'gridRequest.Start',
            this.eventGirdRequestStart.bind(this),
        );

        document.addEventListener(
            'gridRequest.End',
            this.eventGirdRequestEnd.bind(this),
        );

        document.addEventListener('gridRequest.End', this.boundPageInitialized);

        document.addEventListener(
            'gridPeriod.Change',
            this.eventPeriodChange.bind(this),
        );

        document.addEventListener(
            'resetData.Start',
            this.eventResetDataStartHandler.bind(this),
        );

        document.addEventListener(
            'resetData.End',
            this.eventResetDataEndHandler.bind(this),
        );
    }

    /**
     * Initialize instances of various classes.
     */
    initializeClassesInstances() {
        this.DatabaseDataInstance = new DataDatabase(
            this.CURRENT_DATABASE_SLUG,
        );

        // initialize dataLayer tracker class
        this.DataLayerTrackerInstance = new DataLayerTracker(
            {
                state: '#area_type_1_select',
                msa: '#area_type_2_select',
                md: '#area_type_3_select',
            },
            {
                period: '.js-period-button.a-btn--style-closed-type-01 .a-btn__label',
            },
            {},
        );
    }

    /**
     * Initialize the period selection instance.
     */
    initializePeriodSelectsInstance() {
        this.PeriodSelectsInstance = new HousingReportPeriodSelects(this);
    }

    /**
     * Asynchronously initialize the geography selection instance.
     */
    async initializeGeoSelectsInstance() {
        const statesGeoData = await this.DatabaseDataInstance.getSelectedData();
        this.GeoSelectsInstance = new HousingReportGeoSelect(
            this,
            statesGeoData,
        );
    }

    /**
     * Initialize the data grid instance.
     */
    initializeGrid() {
        this.GridInstance = new HousingReportGrid(this);
    }

    /**
     * Initialize the MapImage instance.
     */
    initializeMap() {
        this.MapInstance = new HousingReportMap(this);
    }

    /**
     * Event handler for the 'gridRequest.Start' event.
     * Disables geography and period selection.
     */
    eventGirdRequestStart() {
        this.setLoaders();
        this.checkParams();
        this.GeoSelectsInstance.disableSelects();
        this.PeriodSelectsInstance.disableSelects();

        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.pushFilters();
        }
    }

    /**
     * Event handler for the 'gridRequest.End' event.
     * Enables geography and period selection and updates chart and map data.
     * @param {Event} event - The 'gridRequest.End' event object.
     */
    eventGirdRequestEnd(event) {
        const type = event.detail.type;
        const data = event.detail.data;

        this.GeoSelectsInstance.enableSelects();
        this.PeriodSelectsInstance.enableSelects();
        this.GeoSelectsInstance.resetBtnAvailability(
            Boolean(data.rowData.length),
        );

        if (type === 'success') {
            this.setMap(data.map);
            this.GeoSelectsInstance.setQueryParameters();
        }
        // show Grid/Chart content
        this.initializationEnds(Boolean(data.rowData.length));
        // Need to resize forcefully in order to exclude the small Mapbox.
        this.MapInstance.resize();
        //
        this.removeLoaders();
    }

    /**
     * Event handler for the 'gridPeriod.Change' event.
     * Refreshes the data grid.
     */
    eventPeriodChange() {
        this.gridRefresh();
    }

    eventResetDataStartHandler() {
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.forbidPushing();
        }
    }

    eventResetDataEndHandler() {
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.allowPushing();
            this.DataLayerTrackerInstance.pushFilters({
                reset_bnt: 'Reset Button',
            });
        }
    }

    pageInitialized() {
        super.pageInitialized();
        document.removeEventListener(
            'gridRequest.End',
            this.boundPageInitialized,
        );
        if (this.DataLayerTrackerInstance instanceof DataLayerTracker) {
            this.DataLayerTrackerInstance.allowPushing();
        }
    }

    checkParams() {
        const geoData = this.getGeoRequestParams();
        let period = this.getActivePeriod();

        if (geoData && period) {
            this.PeriodSelectsInstance.allowAllPeriods();
            if (geoData.geoTypeId === 1) {
                // "State" do not have "quarterly" period, so switch to "monthly"
                this.PeriodSelectsInstance.setPeriod('monthly');
                this.PeriodSelectsInstance.forbidPeriod('quarterly');
            } else if (geoData.geoTypeId === 3) {
                // "Metropolitan Division" do not have "monthly" period, so switch to "quarterly"
                this.PeriodSelectsInstance.setPeriod('quarterly');
                this.PeriodSelectsInstance.forbidPeriod('monthly');
            }
        }
    }

    /**
     * Get request parameters for a specific request.
     *
     * @param {Object} request - The request object to which parameters will be added.
     * @returns {Object|boolean} The request object with parameters or `false` if missing required data.
     */
    getRequestParams(request) {
        const geoData = this.getGeoRequestParams();
        let period = this.getActivePeriod();

        // Check if either geoData or period is missing, return false if any is missing
        if (!geoData || !period) {
            return false;
        }

        request['geoData'] = geoData;
        request['tableTypes'] = {
            period: period,
        };

        return request;
    }

    /**
     * Get the active period from the period selection instance.
     * @returns {string} - The active period (e.g., 'annual' or 'monthly').
     */
    getActivePeriod() {
        return this.PeriodSelectsInstance.getActivePeriod();
    }

    /**
     * Get the request parameters for geography selection from the geography selection instance.
     * @returns {Object} - The request parameters for geography selection.
     */
    getGeoRequestParams() {
        return this.GeoSelectsInstance.getRequestParams();
    }

    /**
     * Refresh the data grid.
     */
    gridRefresh() {
        if (this.GridInstance instanceof HousingReportGrid) {
            this.GridInstance.refresh();
        }
    }

    /**
     * Get the grid API instance from the data grid.
     * @returns {Object} - The grid API instance.
     */
    getGridApi() {
        return this.GridInstance.gridAPI;
    }

    /**
     * Set map data in the map instance.
     * @param {Object} mapData - The data to be set in the map.
     */
    setMap(mapData) {
        this.MapInstance.setMap(mapData);
    }
}
/**
 * Export a function to initialize the HousingReport module on a specific page.
 */
function exportHousingReport() {
    let checkPage = document.querySelector('.o-data-page-single-2');

    if (checkPage) {
        new HousingReport();
    }
}

exportHousingReport();
